
/**
 * Platform Tracking Library - Bookmark & Progress Management
 * 
 * This file handles ONLY bookmark and progress tracking.
 * Analytics tracking has been moved to @/lib/analytics/client
 * 
 * RESPONSIBILITIES:
 * - Reading position tracking
 * - Progress calculation
 * - Background sync
 * - Bookmark management
 * 
 * FOR ANALYTICS USE:
 * import { startSession, trackButton, trackCue, trackPage } from '@/lib/analytics/client'
 */

import { loadBookmark } from "@/lib/bookmark";

// Simplified tracking state for progress/bookmarks only
interface TrackingState {
  isTracking: boolean;
  token: string | null;
  startTime: number;
  pausedTime: number;
  lastSyncTime: number;
  currentChapter: number;
  currentSpread: number;
  lastKnownPage: number;
  isVisible: boolean;
}

// Global tracking state
let trackingState: TrackingState = {
  isTracking: false,
  token: null,
  startTime: 0,
  pausedTime: 0,
  lastSyncTime: 0,
  currentChapter: 1,
  currentSpread: 0,
  lastKnownPage: 1,
  isVisible: true,
};

// Background sync interval
let syncInterval: NodeJS.Timeout | null = null;

/**
 * Get the appropriate token for book tracking
 */
function getBookAccessToken(): string | null {
  const bookAccessToken = sessionStorage.getItem('book_access_token');
  
  if (bookAccessToken) {
    return bookAccessToken;
  }
  
  const urlParams = new URLSearchParams(window.location.search);
  const tokenFromUrl = urlParams.get('token');
  
  if (tokenFromUrl) {
    sessionStorage.setItem('book_access_token', tokenFromUrl);
    return tokenFromUrl;
  }
  
  console.log('[Platform Tracking] No book access token found');
  return null;
}

/**
 * Initialize tracking with correct token type
 */
export function initializePlatformTracking(): boolean {
  try {
    const bookAccessToken = getBookAccessToken();
    
    if (!bookAccessToken) {
      console.log('[Platform Tracking] No book access token found');
      return false;
    }
    
    const now = Date.now();
    
    trackingState = {
      isTracking: true,
      token: bookAccessToken,
      startTime: now,
      pausedTime: 0,
      lastSyncTime: now,
      currentChapter: 1,
      currentSpread: 0,
      lastKnownPage: 1,
      isVisible: true,
    };
    
    console.log('[Platform Tracking] Initialized');
    
    setupVisibilityTracking();
    startBackgroundSync();
    
    setTimeout(() => {
      sendProgressUpdate();
    }, 1000);
    
    return true;
  } catch (error) {
    console.error('[Platform Tracking] Initialization failed:', error);
    return false;
  }
}

/**
 * Initialize tracking with authentication
 */
export function initializeTrackingWithAuth(authToken: string, bookAccessToken?: string): boolean {
  try {
    if (bookAccessToken) {
      sessionStorage.setItem('book_access_token', bookAccessToken);
    }
    
    sessionStorage.setItem('auth_token', authToken);
    
    try {
      const tokenParts = authToken.split('.');
      const payload = JSON.parse(atob(tokenParts[1]));
      console.log('[Platform Tracking] Auth integration - User ID extracted:', {
        userId: payload.userId,
        email: payload.email,
        hasBookToken: !!bookAccessToken
      });
    } catch (decodeError) {
      console.warn('[Platform Tracking] Could not decode user info from auth token');
    }
    
    const success = initializePlatformTracking();
    
    if (success) {
      console.log('[Platform Tracking] Successfully initialized with user authentication');
    }
    
    return success;
  } catch (error) {
    console.error('[Platform Tracking] Auth integration failed:', error);
    return false;
  }
}

/**
 * Get auth token when needed for API calls
 */
export function getAuthToken(): string | null {
  return localStorage.getItem('auth_token') || 
         sessionStorage.getItem('auth_token');
}

/**
 * Setup visibility tracking for pause/resume functionality
 */
function setupVisibilityTracking(): void {
  const handleVisibilityChange = () => {
    if (document.hidden) {
      trackingState.isVisible = false;
      trackingState.pausedTime = Date.now();
    } else {
      trackingState.isVisible = true;
      if (trackingState.pausedTime > 0) {
        const pauseDuration = Date.now() - trackingState.pausedTime;
        trackingState.startTime += pauseDuration;
        trackingState.pausedTime = 0;
      }
    }
  };
  
  document.addEventListener('visibilitychange', handleVisibilityChange);
}

/**
 * Start background sync - syncs progress to Neon database
 */
function startBackgroundSync(): void {
  console.log('[Background Sync] ENABLED – Syncing progress to Neon');
  
  if (syncInterval) {
    clearInterval(syncInterval);
  }
  
  syncInterval = setInterval(() => {
    if (trackingState.isTracking && trackingState.isVisible) {
      sendProgressUpdate();
    }
  }, 30000);
}

/**
 * Stop background sync
 */
export function stopPlatformTracking(): void {
  if (syncInterval) {
    clearInterval(syncInterval);
    syncInterval = null;
  }
  
  if (trackingState.isTracking) {
    sendProgressUpdate();
  }
  
  trackingState.isTracking = false;
}

/**
 * Calculate current reading progress percentage
 */
function calculateProgress(): number {
  const totalSpreads = 55;
  const currentSpread = ((trackingState.currentChapter - 1) * 9) + trackingState.currentSpread;
  return Math.min(100, Math.round((currentSpread / totalSpreads) * 100));
}

/**
 * Calculate total reading time in minutes
 */
function calculateReadingTime(): number {
  if (!trackingState.startTime) return 0;
  
  const currentTime = trackingState.isVisible ? Date.now() : trackingState.pausedTime;
  const totalTime = currentTime - trackingState.startTime;
  return Math.round(totalTime / (1000 * 60));
}

/**
 * Get current chapter name
 */
function getCurrentChapterName(): string {
  const chapterNames = [
    "Introduction",
    "The Still Pond",
    "The Colorful Chameleon", 
    "The Brave Bridge",
    "The Kindness Meadow",
    "The Gratitude Garden",
    "The Sharing Tree",
    "Conclusion"
  ];
  
  return chapterNames[trackingState.currentChapter] || `Chapter ${trackingState.currentChapter}`;
}

/**
 * Send progress update to Neon database
 */
export async function sendProgressUpdate(): Promise<boolean> {
  if (!trackingState.isTracking || !trackingState.token) {
    return false;
  }
  
  try {
    const bookmarkData = loadBookmark();
    
    let formattedBookmarks: Array<{ page: number; chapter?: string; note?: string }> | undefined;
    if (bookmarkData) {
      formattedBookmarks = [{
        page: bookmarkData.spreadIndex || 1,
        chapter: bookmarkData.chapterTitle || getCurrentChapterName(),
        note: `Reading position: Chapter ${bookmarkData.chapterId}, Spread ${bookmarkData.spreadIndex}`
      }];
    }
    
    const trackingData = {
      token: trackingState.token,
      progress: Math.max(0, Math.min(100, calculateProgress())),
      currentPage: Math.max(1, trackingState.lastKnownPage),
      currentChapter: getCurrentChapterName(),
      timeSpent: Math.max(0, calculateReadingTime()),
      bookmarks: formattedBookmarks
    };
    
    const response = await fetch('/api/book-access/update-progress', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(trackingData)
    });
    
    if (response.ok) {
      trackingState.lastSyncTime = Date.now();
      console.log('[Platform Tracking] Progress updated successfully');
      return true;
    } else {
      const errorText = await response.text();
      console.error('[Platform Tracking] Failed to update progress:', response.status, errorText);
      return false;
    }
  } catch (error) {
    console.error('[Platform Tracking] Error updating progress:', error);
    return false;
  }
}

/**
 * Update the current reading position
 */
export async function updateCurrentPosition(chapter: number, spread: number): Promise<void> {
  trackingState.currentChapter = chapter;
  trackingState.currentSpread = spread;
  trackingState.lastKnownPage = ((chapter - 1) * 9) + spread;

  await sendProgressUpdate();
}

/**
 * Get the current reading position
 */
export async function getCurrentPosition(): Promise<{
  chapter: number;
  spread: number;
} | null> {
  return {
    chapter: trackingState.currentChapter,
    spread: trackingState.currentSpread
  };
}

/**
 * Get reading analytics summary
 */
export async function getAnalyticsSummary(userId?: string): Promise<any> {
  return {
    totalReadingTime: calculateReadingTime(),
    currentProgress: calculateProgress(),
    lastPosition: {
      chapter: trackingState.currentChapter,
      spread: trackingState.currentSpread
    }
  };
}
